<template>
  <div>
    <div class="reportTable">
      <div class="options-container">
        <!-- Left column with filters and options -->
        <div class="filters-column">
          <!-- Status and date filters -->
          <div class="filter-row">
            <div class="filter-item">
              <p><b>Status</b></p>
              <select
                @change="handleStatusParamChange($event)"
                class="filter-select"
              >
                <option value="">Wszystkie</option>
                <option
                  v-for="status in statuses"
                  :key="status"
                  :value="status"
                >
                  {{ status }}
                </option>
              </select>
            </div>
          </div>

          <!-- Date order filter -->
          <div class="filter-item date-filter">
            <div>
              <p><b>Data zamówienia</b></p>
            </div>

            <div class="datepickers">
              <div class="datepicker-wrapper">
                <datepicker
                  :monday-first="true"
                  :language="pl"
                  v-model="selectedDateFrom"
                  name="dateFrom"
                  :placeholder="'Od'"
                  class="datepicker-input"
                ></datepicker>
                <button
                  v-if="selectedDateFrom"
                  type="button"
                  class="clear-date-btn"
                  @click="
                    selectedDateFrom = null;
                    fetchUserSubscriptions();
                  "
                  title="Wyczyść datę"
                >
                  <i class="fa fa-times"></i>
                </button>
              </div>
              <div class="datepicker-wrapper">
                <datepicker
                  :language="pl"
                  v-model="selectedDateTo"
                  name="dateTo"
                  :placeholder="'Do'"
                  class="datepicker-input"
                ></datepicker>
                <button
                  v-if="selectedDateTo"
                  type="button"
                  class="clear-date-btn"
                  @click="
                    selectedDateTo = null;
                    fetchUserSubscriptions();
                  "
                  title="Wyczyść datę"
                >
                  <i class="fa fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Category, Subcategory, Service filters -->
          <div class="filter-section">
            <div
              class="filter-item"
              v-if="
                recurringOrdersReportFilters.categories &&
                recurringOrdersReportFilters.categories.length > 1
              "
            >
              <p><b>Kategoria</b></p>
              <div class="multi-select-container">
                <div
                  v-for="category in recurringOrdersReportFilters.categories"
                  :key="category.id"
                  class="multi-select-item"
                >
                  <label>
                    <input
                      type="checkbox"
                      :value="category.id"
                      data-type="category"
                      :checked="isCategorySelected(category.id)"
                      @change="handleCategoryChange($event, category.id)"
                    />
                    <span>{{ category.display_name }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="filter-item">
              <p><b>Podkategoria</b></p>
              <div class="multi-select-container">
                <div
                  v-for="subcategory in filteredSubcategories"
                  :key="subcategory.id"
                  class="multi-select-item"
                >
                  <label>
                    <input
                      type="checkbox"
                      :value="subcategory.id"
                      data-type="subcategory"
                      :checked="isSubcategorySelected(subcategory.id)"
                      @change="handleSubcategoryChange($event, subcategory.id)"
                    />
                    <span
                      >{{ subcategory.display_name }} ({{
                        subcategory.category_display_name
                      }})
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div class="filter-item">
              <p><b>Usługa</b></p>
              <div class="multi-select-container">
                <div
                  v-for="service in filteredServices"
                  :key="service.id"
                  class="multi-select-item"
                >
                  <label>
                    <input
                      type="checkbox"
                      :value="service.id"
                      data-type="service"
                      :checked="isServiceSelected(service.id)"
                      @change="handleServiceChange($event, service.id)"
                    />
                    <span
                      >{{ service.display_name }} ({{
                        service.subcategory_display_name
                      }}
                      - {{ service.category_display_name }})
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div
              class="filter-item"
              v-if="
                recurringOrdersReportFilters.employees &&
                recurringOrdersReportFilters.employees.length > 0
              "
            >
              <p><b>Wykonawca</b></p>
              <div class="multi-select-container">
                <div
                  v-for="employee in recurringOrdersReportFilters.employees"
                  :key="employee.id"
                  class="multi-select-item"
                >
                  <label>
                    <input
                      type="checkbox"
                      :value="employee.id"
                      data-type="employee"
                      :checked="isEmployeeSelected(employee.id)"
                      @change="handleEmployeeChange($event, employee.id)"
                    />
                    <span>{{ employeeDisplayName(employee) }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right column with calendars -->
        <div v-if="!embedded" class="calendars-column">
          <!-- Glovo report calendar -->
          <div class="calendar-box">
            <button
              class="btn button btn-outline-dark calendar-button"
              type="button"
              data-toggle="collapse"
              data-target="#collapseGlovoReport"
              aria-expanded="false"
              aria-controls="collapseGlovoReport"
              @click="toggleCollapse('collapseGlovoReport')"
            >
              <span>Generowanie raportu Glovo</span>
              <i
                class="fa"
                :class="
                  collapseState.collapseGlovoReport
                    ? 'fa-angle-up'
                    : 'fa-angle-down'
                "
              ></i>
            </button>

            <div class="collapse" id="collapseGlovoReport">
              <div
                class="filter-item"
                v-if="
                  recurringOrdersReportFilters.categories &&
                  recurringOrdersReportFilters.categories.length > 1
                "
              >
                <p><b>Kategoria</b></p>
                <select
                  style="width: 100%"
                  v-model="selectedCategoryForGlovoReport"
                  class="filter-select"
                >
                  <option :value="null">Wszystkie</option>
                  <option
                    v-for="category in recurringOrdersReportFilters.categories"
                    :key="category.id"
                    :value="category.id"
                  >
                    {{ category.display_name }}
                  </option>
                </select>
              </div>
              <div
                class="filter-item"
                v-if="
                  glovoAddressBookProfiles.length > 0
                "
              >
                <p><b>Address Book ID</b></p>
                <div class="multi-select-container">
                  <div class="multi-select-item">
                    <label>
                      <input
                        type="checkbox"
                        :checked="selectedAddressBookIdsForGlovo.length === 0"
                        @change="handleAllAddressBookIdsChange($event)"
                      />
                      <span>Wszystkie</span>
                    </label>
                  </div>
                  <div
                    v-for="(profile, index) in glovoAddressBookProfiles"
                    :key="`${profile.glovo_address_book_id}_${index}`"
                    class="multi-select-item"
                  >
                    <label>
                      <input
                        type="checkbox"
                        :value="profile.glovo_address_book_id"
                        :checked="
                          selectedAddressBookIdsForGlovo.includes(
                            profile.glovo_address_book_id
                          )
                        "
                        @change="
                          handleAddressBookIdChange(
                            $event,
                            profile.glovo_address_book_id
                          )
                        "
                      />
                      <span
                        >{{ profile.first_name }} {{ profile.last_name }} ({{
                          profile.glovo_address_book_id
                        }})</span
                      >
                    </label>
                  </div>
                </div>
              </div>
              <div class="card card-body">
                <div class="calendar-container">
                  <v-calendar
                    @dayclick="onDayClick"
                    :attributes="calendarAttributes"
                    expanded
                    class="full-width-calendar"
                  >
                  </v-calendar>
                </div>
                <button
                  class="btn button btn-outline-dark"
                  @click="generateGlovoReport"
                >
                  Generuj raport Glovo
                </button>
              </div>
            </div>
          </div>

          <!-- Excluded days calendar -->
          <div class="calendar-box">
            <button
              class="btn button btn-outline-dark calendar-button"
              type="button"
              data-toggle="collapse"
              data-target="#collapseExcludedDays"
              aria-expanded="false"
              aria-controls="collapseExcludedDays"
              @click="toggleCollapse('collapseExcludedDays')"
            >
              <span>Wykluczone dni</span>
              <i
                class="fa"
                :class="
                  collapseState.collapseExcludedDays
                    ? 'fa-angle-up'
                    : 'fa-angle-down'
                "
              ></i>
            </button>
            <div class="collapse" id="collapseExcludedDays">
              <div
                class="filter-item"
                v-if="
                  recurringOrdersReportFilters.categories &&
                  recurringOrdersReportFilters.categories.length > 1
                "
              >
                <p><b>Kategoria</b></p>
                <select
                  style="width: 100%"
                  v-model="selectedCategoryForExcludedDays"
                  class="filter-select"
                >
                  <option
                    v-for="category in recurringOrdersReportFilters.categories"
                    :key="category.id"
                    :value="category.id"
                  >
                    {{ category.display_name }}
                  </option>
                </select>
              </div>

              <div class="card card-body">
                <div class="calendar-container">
                  <v-calendar
                    @dayclick="onDayClickExcludedDay"
                    :attributes="excludedDaysAttributes"
                    expanded
                    class="full-width-calendar"
                  >
                  </v-calendar>
                </div>
                <button
                  class="btn button btn-outline-dark"
                  @click="saveExcludedDays"
                >
                  Zapisz zmiany wykluczonych dni
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Nagłówek i pasek paginacji MUSZĄ zostać na zewnątrz .table-container: ten kontener ma
           overflow-x: auto, a tabela jest od niego szersza, więc w środku pasek urywałby się na
           szerokości ekranu zamiast obejmować całą tabelę. Tak samo jest w innych listach panelu,
           gdzie TableHeader stoi przed kontenerem tabeli. -->
      <!-- Na karcie użytkownika tytuł pomijamy - zakładka jest już opisana w nawigacji karty,
           tak jak w siostrzanych zakładkach (Zamówienia). Reszta nagłówka jest wspólna. -->
      <div class="searchPanel">
        <div class="flex-inline">
          <h1 class="title" v-if="!embedded">Obiady</h1>
          <button
            v-if="!embedded"
            :disabled="loading"
            class="btn button btn-outline-dark headerPaymentsButton"
            data-toggle="modal"
            data-target="#handle-payments-confirm"
          >
            Pobierz płatności dla wszystkich aktywnych zamówień, które wymagają
            przedłużenia
          </button>
          <div
            class="spinner-border text-secondary loader"
            role="status"
            v-show="loading === true"
          >
            <span class="sr-only">Loading...</span>
          </div>
        </div>
        <search-input
          :placeholder="'Szukaj...'"
          :defaultValue="searchParam"
          @search="handleSearch"
        ></search-input>
      </div>

      <custom-modal
        v-if="!embedded"
        title="Pobieranie płatności"
        id="handle-payments-confirm"
        :hideDefaultFooter="true"
      >
        <template #content>
          <div class="confirm-container">
            <p class="confirm-question">
              Czy na pewno chcesz pobrać płatności dla wszystkich zamówień,
              które wymagają przedłużenia?
            </p>
            <div class="confirm-actions">
              <button
                class="btn button btn-outline-dark"
                :disabled="loading"
                data-dismiss="modal"
                @click="handlePaymentsForSubscriptions()"
              >
                Tak
              </button>
              <button class="btn btn-outline-dark" data-dismiss="modal">
                Nie
              </button>
            </div>
          </div>
        </template>
      </custom-modal>

      <custom-modal
        title="Pobieranie płatności"
        id="handle-single-payment-confirm"
        :hideDefaultFooter="true"
      >
        <template #content>
          <div class="confirm-container">
            <p class="confirm-question">
              Czy na pewno chcesz pobrać płatność i przedłużyć abonament
              <template v-if="paymentsConfirmSub">
                dla zamówienia {{ paymentsConfirmSub.firstName }}
                {{ paymentsConfirmSub.lastName }} ({{
                  paymentsConfirmSub.orderId
                }})</template
              >?
            </p>
            <!-- Pojedyncze pobranie nie sprawdza, czy bieżący tydzień już się zaczął (robi to tylko przebieg
                 zbiorczy), więc abonament może zostać przedłużony przed czasem - stąd ostrzeżenie. -->
            <p class="confirm-hint">
              Środki zostaną pobrane od razu, także wtedy, gdy bieżący tydzień
              jeszcze się nie skończył.
            </p>
            <div class="confirm-actions">
              <button
                class="btn button btn-outline-dark"
                :disabled="loading"
                data-dismiss="modal"
                @click="confirmSinglePayment()"
              >
                Tak
              </button>
              <button class="btn btn-outline-dark" data-dismiss="modal">
                Nie
              </button>
            </div>
          </div>
        </template>
      </custom-modal>

      <button
        id="paymentsSummaryOpener"
        type="button"
        style="display: none"
        data-toggle="modal"
        data-target="#handle-payments-summary"
      ></button>

      <custom-modal
        title="Podsumowanie pobrania płatności"
        id="handle-payments-summary"
      >
        <template #content>
          <div v-if="paymentsSummary">
            <p v-if="!paymentsSummary.total" class="summary-empty">
              Brak zamówień wymagających przedłużenia - nie pobrano żadnej
              płatności.
            </p>
            <template v-else>
              <div class="summary-counters">
                <span
                  v-for="group in paymentsSummaryGroups"
                  :key="group.outcome"
                  class="summary-counter"
                >
                  {{ group.label }}: <b>{{ group.items.length }}</b>
                </span>
              </div>

              <div
                v-for="group in paymentsSummaryGroups"
                :key="`table_${group.outcome}`"
                class="summary-group"
              >
                <p class="summary-group-title">
                  {{ group.label }} ({{ group.items.length }})
                </p>
                <table class="table borderless table-sm summary-table">
                  <thead>
                    <tr>
                      <th>Imię</th>
                      <th>Nazwisko</th>
                      <th>Numer telefonu</th>
                      <th>Telefon zamawiającego</th>
                      <th>Usługa</th>
                      <th>Kwota</th>
                      <th>ID zamówienia</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in group.items" :key="item.orderId">
                      <td>{{ item.recipientFirstName || "-" }}</td>
                      <td>{{ item.recipientLastName || "-" }}</td>
                      <td>{{ formatPhoneNumber(item.recipientPhone) || "-" }}</td>
                      <td>
                        {{ formatPhoneNumber(item.orderingAccountPhone) || "-" }}
                      </td>
                      <td>{{ item.serviceName || "-" }}</td>
                      <td>{{ summaryAmountLabel(item) }}</td>
                      <td>{{ item.orderId }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </template>
      </custom-modal>

      <div class="header">
        <!-- Licznik pokazuje SUBSKRYPCJE, nie wiersze: strona zawiera komplet tygodni swoich
             subskrypcji, więc wierszy w tabeli bywa więcej niż on_page.
             dateFrom/dateTo MUSZĄ być podane jawnie: PaginationInline wstawia je do payloadu ze
             swoich propsów, więc bez tego nadpisałby datami pustymi to, co niesie extraParams,
             i zmiana strony gubiłaby wybrany zakres. Frazę niesie searchParam - w extraParams
             siedzi pod filter_param, które ten sam mechanizm by wyzerował. -->
        <div class="pagination">
          <pagination-inline
            :dispatchAction="'fetchRecurringOrdersReport'"
            :onPage="pagination.on_page"
            :pageNumber="pagination.page_number"
            :total="pagination.total"
            :searchParam="searchParam"
            :dateFrom="reportFilterParams.date_from"
            :dateTo="reportFilterParams.date_to"
            :extraParams="reportFilterParams"
            v-model="loading"
          ></pagination-inline>
        </div>
        <!-- Na karcie użytkownika zostaje jeden przycisk: drugi raport (użytkownicy bez ponowienia)
             jest globalny i na zakładce jednego podopiecznego nie miałby sensu. -->
        <div
          class="headerActions"
          v-if="embedded && subscriptionReport && subscriptionReport.length > 0"
        >
          <button
            class="btn button btn-outline-dark downloadReportButton"
            @click="downloadSubscriptionsReport"
          >
            Pobierz raport z zamówieniami
          </button>
        </div>
        <!-- Lista raportów jest widoczna zawsze, także przy pustej tabeli: raport "bez ponowienia"
             nie zależy od tego, co widać na liście (liczy okres z dnia pobrania i ignoruje filtry). -->
        <div class="headerActions" v-if="!embedded">
          <label class="reportLabel" for="reportTypeForDownload">Raport</label>
          <select
            id="reportTypeForDownload"
            class="filter-select reportSelect"
            :value="selectedReportTypeForDownload"
            @change="handleReportTypeForDownload($event)"
          >
            <option value="">Wybierz raport do pobrania</option>
            <option
              v-for="reportType in reportTypesForDownload"
              :key="reportType.value"
              :value="reportType.value"
            >
              {{ reportType.label }}
            </option>
          </select>
          <!-- Zaznaczenie zawęża oba raporty, a nie widać tego po samej liście - stąd podpowiedź. -->
          <span v-if="selectedOrders.length > 0" class="reportSelectionHint">
            z {{ selectedOrders.length }} zaznaczonych
          </span>
          <button
            class="btn button btn-outline-dark downloadReportButton"
            :disabled="!selectedReportTypeForDownload"
            @click="downloadSelectedReport"
          >
            Pobierz
          </button>
        </div>
      </div>

      <!-- Table section -->
      <div class="table-container">
        <div v-if="!loading">
          <table
            class="table borderless"
            v-if="subscriptionReport && subscriptionReport.length > 0"
          >
            <thead>
              <tr class="tr-table">
                <!-- Akcje masowe stoją w nagłówku pierwszej kolumny, tak samo jak w Historii usług.
                     "Zaznacz wszystkie" obejmuje wiersze WIDOCZNE (główne + rozwinięte tygodnie) - nic
                     nie zaznacza się po cichu w zwiniętych grupach. -->
                <th v-if="!embedded" class="selectionColumn">
                  <div class="selectionActions">
                    <button
                      class="btn btn-sm btn-outline-dark selectionButton"
                      @click="toggleSelectAllVisibleOrders"
                    >
                      {{
                        allVisibleOrdersSelected
                          ? "Odznacz wszystkie"
                          : "Zaznacz wszystkie"
                      }}
                    </button>
                    <button
                      v-if="selectedOrders.length > 0"
                      class="btn btn-sm btn-outline-dark selectionButton"
                      data-toggle="modal"
                      data-target="#obiady-bulk-status"
                    >
                      Zmień status
                    </button>
                    <button
                      v-if="selectedOrders.length > 0"
                      class="btn btn-sm btn-outline-dark selectionButton"
                      data-toggle="modal"
                      data-target="#obiady-bulk-payments"
                    >
                      Pobierz płatności
                    </button>
                  </div>
                </th>
                <th>ID</th>
                <th></th>
                <th>Imię i nazwisko</th>
                <th>Miasto</th>
                <th>Kategoria</th>
                <th>Podkategoria, Opcja</th>
                <th>Wykonawca</th>
                <th>Data zamówienia</th>
                <th>Utworzone przez</th>
                <th>Ilość dni wydań</th>
                <th>Data rozpoczęcia</th>
                <th>Data zakończenia</th>
                <th>Data ostatniej płatności</th>
                <th>Czy opłacone na kolejny tydzień?</th>
                <th>Metoda płatności</th>
                <th>Numer telefonu</th>
                <th>Data modyfikacji</th>
                <th>Cena</th>
                <th>Status</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="sub in visibleSubscriptionRows"
                :key="sub.orderId"
                :class="
                  !sub.isMainRow ||
                  showedMoreSubscriptionOrders.includes(`${sub.firstOrderId}`)
                    ? `nested-subscription ${
                        !sub.isPaidForNextPeriod &&
                        sub.orderState === 'ACCEPTED' &&
                        isNowDateAfterOrderStartDate(sub.subscriptionStart)
                          ? 'not-renewed'
                          : ''
                      } ${
                        sub.hasRecurringOrder && !sub.isRecurringOrderActive
                          ? 'subscription-cancelled'
                          : ''
                      } ${sub.isFirstOrderRow ? 'first-order-row' : ''} ${
                        sub.isLastOrderRow ? 'last-order-row' : ''
                      }`
                    : !sub.isPaidForNextPeriod &&
                      sub.orderState === 'ACCEPTED' &&
                      sub.hasRecurringOrder &&
                      isNowDateAfterOrderStartDate(sub.subscriptionStart)
                    ? 'not-renewed'
                    : sub.hasRecurringOrder && !sub.isRecurringOrderActive
                    ? 'subscription-cancelled'
                    : ''
                "
              >
                <!-- Zaznaczenie trzymamy w komponencie (selectedOrders), a nie we właściwości wiersza:
                     wiersze przychodzą ze store'a, a pola dopisywane do nich po fakcie nie są w Vue 2
                     reaktywne. Przy okazji zaznaczenie przeżywa zmianę strony. -->
                <td v-if="!embedded">
                  <input
                    type="checkbox"
                    class="order-checkbox"
                    :checked="isOrderSelected(sub.orderId)"
                    @change="toggleOrderSelection(sub)"
                  />
                </td>
                <td>
                  <img
                    @click="copyText(sub.orderId)"
                    style="cursor: pointer"
                    src="@/icons/copy-icons.svg"
                    alt=""
                  />
                </td>
                <td>
                  <div
                    :style="
                      addedMainRow[`${sub.firstOrderId}`] > 1 && sub.isMainRow
                        ? 'display: flex; gap: 1rem'
                        : 'visibility: hidden'
                    "
                  >
                    <i
                      v-if="
                        !showedMoreSubscriptionOrders.includes(
                          `${sub.firstOrderId}`
                        )
                      "
                      class="fa fa-arrow-circle-down arrow-icon"
                      aria-hidden="true"
                      @click="
                        showMoreSubscriptionsOrders(`${sub.firstOrderId}`)
                      "
                    ></i>
                    <i
                      v-else
                      class="fa fa-arrow-circle-up arrow-icon"
                      aria-hidden="true"
                      @click="
                        hideMoreSubscriptionsOrders(`${sub.firstOrderId}`)
                      "
                    ></i>
                    <span>
                      {{
                        subscriptionReport.filter(
                          (s) => `${s.firstOrderId}` === `${sub.firstOrderId}`
                        ).length
                      }}
                    </span>
                  </div>
                </td>

                <td>{{ sub.firstName }} {{ sub.lastName }}</td>
                <td>{{ sub.city }}</td>
                <td>{{ sub.serviceCategory }}</td>
                <td>{{ sub.serviceSubcategory }}, {{ sub.service }}</td>
                <td>{{ sub.employeeName }}</td>
                <td>{{ formattedDateWithoutTime(sub.orderDate) }}</td>
                <td>{{ sub.createdBy || "-" }}</td>
                <td>{{ sub.numberOfDays }}</td>
                <td>{{ formattedDateWithoutTime(sub.subscriptionStart) }}</td>
                <td>
                  {{ formattedDateWithoutTime(sub.subscriptionEnd) }}
                </td>
                <td>
                  {{
                    sub.isMainRow && sub.lastPaymentDate
                      ? formattedDateWithTime(sub.lastPaymentDate)
                      : "-"
                  }}
                </td>
                <td>
                  {{
                    !sub.hasRecurringOrder
                      ? "Usługa jednorazowa"
                      : sub.orderState === "ACCEPTED"
                      ? sub.isPaidForNextPeriod
                        ? "Tak"
                        : "Nie"
                      : "-"
                  }}
                </td>
                <td>{{ sub.paymentMethod }}</td>
                <!-- <td>{{ sub.pesel }}</td> -->
                <!-- <td>{{ sub.birthdate }}</td> -->
                <td>{{ formatPhoneNumber(sub.phone) }}</td>

                <!-- <td>{{ sub.gender }}</td>
          <td>{{ sub.email }}</td>
          <td>{{ sub.city }}</td> -->
                <td>
                  {{
                    sub.lastManuallyModificationDate
                      ? formattedDateWithoutTime(
                          sub.lastManuallyModificationDate
                        )
                      : "-"
                  }}
                </td>
                <td>
                  {{ sub.price }}
                </td>
                <td>
                  <div class="inline-buttons">
                    <img
                      :id="`showTooltipButton_${sub.id}`"
                      @mouseover="showSubTooltip(sub)"
                      v-if="sub.isMainRow && sub.plannedAutoChange"
                      style="cursor: pointer"
                      src="@/icons/more-info.svg"
                      alt=""
                      width="40px"
                    />
                    <button
                      v-if="sub.hasRecurringOrder"
                      :disabled="loading"
                      data-toggle="modal"
                      data-target="#handle-single-payment-confirm"
                      @click="askForSinglePaymentConfirmation(sub)"
                      v-show="
                        sub.orderState === 'ACCEPTED' &&
                        !sub.isPaidForNextPeriod
                      "
                      class="button btn btn-outline-dark"
                    >
                      POBIERZ PŁAT.
                    </button>

                    <button
                      v-if="sub.orderState === 'ACCEPTED'"
                      class="button btn btn-outline-dark"
                      @click="selectSub(sub)"
                      data-toggle="modal"
                      data-target="#sub-edit"
                    >
                      {{ sub.orderState }}
                    </button>
                    <button
                      v-else-if="sub.orderState === 'UNREALIZED'"
                      class="button btn btn-outline-danger"
                      @click="selectSub(sub)"
                      data-toggle="modal"
                      data-target="#sub-edit"
                    >
                      {{ sub.orderState }}
                    </button>
                    <button
                      v-else-if="sub.orderState === 'FINISHED'"
                      class="button btn btn-outline-yellow"
                      @click="selectSub(sub)"
                      data-toggle="modal"
                      data-target="#sub-edit"
                    >
                      {{ sub.orderState }}
                    </button>

                    <button
                      class="button btn btn-outline-gradient"
                      @click="selectSub(sub)"
                      data-toggle="modal"
                      data-target="#sub-details"
                    >
                      Więcej
                    </button>

                    <button
                      v-if="
                        sub.isRecurringOrderActive &&
                        sub.orderState === 'ACCEPTED'
                      "
                      class="button btn btn-outline-danger"
                      @click="selectSub(sub)"
                      data-toggle="modal"
                      data-target="#sub-cancel-confirm"
                    >
                      Anuluj abonament
                    </button>
                    <div
                      v-if="
                        sub.hasRecurringOrder && !sub.isRecurringOrderActive
                      "
                    >
                      Abonament anulowany
                    </div>

                    <img
                      class="profile-icon"
                      @click="moveToProfile(sub.userId)"
                      src="../../icons/user.svg"
                    />
                  </div>
                </td>

                <custom-modal
                  :title="`Szczegóły zamówienia '${selectedSub.firstName} ${selectedSub.lastName}' ID: ${selectedSub.orderId}`"
                  id="sub-details"
                >
                  <template #content>
                    <table class="table borderless" v-if="selectedSub">
                      <thead>
                        <tr class="tr-table">
                          <th>Imię i nazwisko</th>
                          <th>Zamawiający</th>
                          <th>Telefon zamawiającego</th>
                          <th>Utworzone przez</th>
                          <th>Data urodzenia</th>
                          <th>Email</th>
                          <th>Miasto</th>
                          <th>Faktura</th>
                          <!-- <th>Informacje</th> -->
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {{ selectedSub.firstName }}
                            {{ selectedSub.lastName }}
                          </td>
                          <td>
                            {{ selectedSub.userFirstName }}
                            {{ selectedSub.userLastName }}
                          </td>
                          <td>{{ selectedSub.userPhone }}</td>
                          <td>{{ selectedSub.createdBy || "-" }}</td>
                          <td>{{ selectedSub.birthdate }}</td>
                          <td>{{ selectedSub.email }}</td>
                          <td>{{ selectedSub.city }}</td>
                          <td>
                            <div
                              class="invoiceButtons"
                              v-if="selectedSub.orderState === 'FINISHED'"
                            >
                              <div class="flex">
                                <img
                                  @click="fetchInvoiceLink(selectedSub.id)"
                                  style="cursor: pointer"
                                  src="@/icons/download.svg"
                                  alt=""
                                />
                                <img
                                  @click="
                                    copyInvoiceLinkToClipboard(selectedSub.id)
                                  "
                                  style="cursor: pointer; margin-left: 5px"
                                  src="@/icons/copy-icons.svg"
                                  alt=""
                                />
                              </div>

                              <a
                                :href="invoiceLink"
                                :id="`downloadLink${selectedSub.id}`"
                                style="visibility: hidden"
                                target="_blank"
                              ></a>
                            </div>
                            <div v-else>Brak</div>
                          </td>
                          <!-- <td>{{ selectedSub.customerNote || "Brak" }}</td> -->
                        </tr>
                      </tbody>
                    </table>
                    <div class="customer-note-container">
                      <p><b>Dodatkowe informacje:</b></p>
                      <div class="customer-note-edit">
                        <textarea
                          v-model="editedCustomerNote"
                          :disabled="!isEditingCustomerNote"
                          :class="{ 'note-editing': isEditingCustomerNote }"
                          placeholder="Brak dodatkowych informacji"
                          rows="3"
                          maxlength="1024"
                        ></textarea>
                        <div class="note-actions">
                          <button
                            v-if="!isEditingCustomerNote"
                            class="btn btn-sm btn-outline-dark"
                            @click="startEditingCustomerNote"
                          >
                            Edytuj
                          </button>
                          <template v-else>
                            <button
                              class="btn btn-sm btn-outline-success"
                              @click="saveCustomerNote"
                              :disabled="loading"
                            >
                              Zapisz
                            </button>
                            <button
                              class="btn btn-sm btn-outline-secondary"
                              @click="cancelEditingCustomerNote"
                            >
                              Anuluj
                            </button>
                          </template>
                        </div>
                        <p
                          v-if="isEditingCustomerNote"
                          class="character-counter"
                        >
                          {{
                            editedCustomerNote ? editedCustomerNote.length : 0
                          }}/1024
                        </p>
                      </div>
                    </div>
                  </template>
                </custom-modal>

                <custom-modal
                  title="Anulowanie abonamentu"
                  id="sub-cancel-confirm"
                >
                  <template #content>
                    <div
                      class="edit-container"
                      v-if="
                        selectedSub.isRecurringOrderActive &&
                        selectedSub.orderState === 'ACCEPTED'
                      "
                    >
                      <p
                        style="
                          font-size: 18px;
                          margin-bottom: 2rem;
                          text-align: center;
                        "
                      >
                        Czy na pewno chcesz anulować abonament?
                      </p>
                      <div class="edit-row" style="justify-content: center">
                        <button
                          class="btn btn-outline-danger"
                          :disabled="cancelSubscriptionLoading"
                          @click="handleCancelSubscription"
                        >
                          Potwierdź
                        </button>
                      </div>
                    </div>
                  </template>
                </custom-modal>

                <custom-modal
                  :title="`Edycja zamówienia '${selectedSub.firstName} ${selectedSub.lastName}' ID: ${selectedSub.orderId}`"
                  id="sub-edit"
                >
                  <template #content>
                    <div class="edit-container">
                      <div class="edit-row">
                        <div class="edit-container-label">Wybierz status</div>
                        <div>
                          <select v-model="editSubStatus">
                            <option
                              v-for="status in statuses"
                              :key="status"
                              :value="status"
                            >
                              {{ status }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div
                        class="edit-row"
                        v-if="decisionOptions && decisionOptions.length > 0"
                      >
                        <div class="edit-container-label">Decyzja</div>
                        <div>
                          <select v-model="editSubDecision">
                            <option
                              v-for="decision in decisionOptions"
                              :key="decision"
                              :value="decision.value"
                            >
                              {{ decision.label }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div
                        class="edit-row"
                        v-if="
                          withInvoiceOptions && withInvoiceOptions.length > 0
                        "
                      >
                        <div class="edit-container-label">
                          Z fakturą czy bez?
                        </div>
                        <div>
                          <select v-model="withCreateInvoice">
                            <option
                              v-for="decision in withInvoiceOptions"
                              :key="decision"
                              :value="decision.value"
                            >
                              {{ decision.label }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div class="edit-row">
                        <div class="edit-container-label">
                          Data zmiany statusu
                        </div>

                        <datepicker
                          :monday-first="true"
                          :language="pl"
                          v-model="editSubDate"
                          name="editSubDate"
                          :placeholder="'Data zmiany'"
                          style="padding: 0"
                        ></datepicker>
                      </div>

                      <div class="edit-row">
                        <div class="edit-container-label">Podaj powód</div>
                      </div>
                      <div
                        class="edit-row"
                        style="
                          position: relative;
                          width: fit-content;
                          margin-bottom: 2rem;
                        "
                      >
                        <textarea
                          v-model="editSubReason"
                          id="editSubReason"
                          type="text"
                          required
                          minlength="1"
                          maxlength="1000"
                          placeholder="Powód"
                          rows="5"
                          cols="80"
                          style="padding: 5px"
                        />
                        <p
                          style="position: absolute; right: 5px; bottom: -25px"
                        >
                          {{ editSubReason.length }}/1000
                        </p>
                      </div>

                      <div class="edit-row">
                        <button
                          class="btn button btn-outline-dark"
                          @click="handleEditSub"
                        >
                          Zapisz
                        </button>
                      </div>
                    </div>
                  </template>
                </custom-modal>
              </tr>
            </tbody>
          </table>
          <h3 v-else class="ml-3"><b>Brak subskrypcji</b></h3>
        </div>
        <div
          class="spinner-border text-secondary loader"
          role="status"
          v-if="loading"
        >
          <span class="sr-only">Loading...</span>
        </div>
      </div>

      <!-- Modale akcji masowych stoją POZA tabelą: w środku znikałyby razem z listą na czas
           przeładowania (v-if="!loading"), a wtedy nie dałoby się ich domknąć po akcji. -->
      <custom-modal
        v-if="!embedded"
        title="Zmiana statusu wielu zamówień"
        id="obiady-bulk-status"
      >
        <template #content>
          <div class="edit-container">
            <p>
              Wybrano {{ selectedOrders.length }} zamówień do zmiany statusu.
            </p>
            <!-- Backend (OrderService.changeOrderStatusForMultipleOrders) zmienia status wyłącznie
                 zamówieniom w stanie WAITING/ACCEPTED, a pozostałe cicho pomija - lepiej powiedzieć
                 to wprost, niż zostawić admina z wrażeniem, że zmiana się nie zapisała. -->
            <p
              v-if="selectedOrders.length !== selectedAcceptedOrdersIds.length"
              class="bulkStatusHint"
            >
              Zmiana obejmie tylko zamówienia w statusie ACCEPTED - pozostałe
              ({{ selectedOrders.length - selectedAcceptedOrdersIds.length }})
              zostaną pominięte.
            </p>
            <div class="edit-row">
              <div class="edit-container-label">Wybierz status</div>
              <div>
                <select v-model="bulkStatusToChange">
                  <option
                    v-for="status in bulkStatuses"
                    :key="status"
                    :value="status"
                  >
                    {{ status }}
                  </option>
                </select>
              </div>
            </div>
            <div class="edit-row">
              <button
                class="btn button btn-outline-dark"
                :disabled="bulkActionLoading || !bulkStatusToChange"
                @click="changeStatusForSelectedOrders"
              >
                Zapisz
              </button>
            </div>
          </div>
        </template>
      </custom-modal>

      <custom-modal
        v-if="!embedded"
        title="Pobranie płatności"
        id="obiady-bulk-payments"
      >
        <template #content>
          <div class="edit-container">
            <p style="font-size: 18px; margin-bottom: 2rem; text-align: center">
              Czy na pewno chcesz pobrać płatności dla
              {{ selectedPayableOrdersIds.length }} zamówień?
            </p>
            <!-- Licznik obejmuje dokładnie te zaznaczone wiersze, które mają w tabeli aktywny przycisk
                 "POBIERZ PŁAT.": status ACCEPTED i nieopłacony kolejny tydzień. Reszta zaznaczenia nie
                 ma czego pobierać - backend i tak by ją odrzucił. -->
            <p
              v-if="selectedPayableOrdersIds.length === 0"
              style="text-align: center"
            >
              Żadne z zaznaczonych zamówień nie kwalifikuje się do pobrania
              płatności na kolejny tydzień.
            </p>
            <div
              class="edit-row"
              style="justify-content: center; gap: 1rem"
            >
              <button
                class="btn btn-outline-dark"
                :disabled="
                  bulkActionLoading || selectedPayableOrdersIds.length === 0
                "
                @click="handlePaymentsForSelectedOrders"
              >
                TAK
              </button>
              <button class="btn btn-outline-danger" data-dismiss="modal">
                NIE
              </button>
            </div>
          </div>
        </template>
      </custom-modal>
    </div>
  </div>
</template>

<script>
import axios from "../../axios";
import shared from "../../shared";
import { pl } from "vuejs-datepicker/dist/locale";
import Datepicker from "vuejs-datepicker";
import CustomModal from "../CustomModal.vue";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light-border.css";
import VCalendar from "v-calendar/lib/components/calendar.umd";
import moment from "moment";
import SearchInput from "../SearchInput.vue";
import PaginationInline from "../PaginationInline.vue";
export default {
  // embedded - widok osadzony w karcie uzytkownika (zakladka "Obiady", SAPP00016):
  // chowa globalne narzedzia (masowe platnosci, raport Glovo, wykluczone dni), ktore
  // nie dotycza pojedynczego podopiecznego. Reszta (filtry + tabela) bez zmian.
  props: ["userId", "embedded"],
  components: {
    Datepicker,
    CustomModal,
    VCalendar,
    SearchInput,
    PaginationInline,
  },
  name: "RecurringOrdersReportsView",
  data() {
    return {
      pl: pl,
      filterType: "",
      selectedDateFrom: null,
      selectedDateTo: null,
      searchParam: "",
      // 20 na stronę - tak jak wszystkie listy w panelu (users, payments, Payouts, SmsList).
      onPage: 20,
      addedMainRow: {},
      showedMoreSubscriptionOrders: [],
      invoiceLink: "",
      selectedOrderStatus: "",
      selectedReportTypeForDownload: "",
      // Wartości są tylko wewnętrznym rozróżnieniem w panelu - backend ma osobny endpoint na każdy raport.
      reportTypesForDownload: [
        { value: "ORDERS", label: "Zamówienia" },
        { value: "NO_RENEWAL", label: "Użytkownicy bez ponowienia zamówienia" },
      ],
      // Zaznaczone wiersze: trzymamy id RAZEM ze statusem, bo zaznaczenie przeżywa zmianę strony,
      // a licznik przy pobieraniu płatności dotyczy tylko zleceń ACCEPTED - także tych z innej strony.
      selectedOrders: [],
      bulkStatusToChange: "",
      bulkActionLoading: false,
      selectedSub: {},
      isEditingCustomerNote: false,
      editedCustomerNote: "",
      originalCustomerNote: "",
      editSubStatus: "ACCEPTED",
      editSubDecision: false,
      editSubReason: "",
      editSubDate: null,
      loading: false,
      cancelSubscriptionLoading: false,
      nestedSubscriptionOpacities: {},
      nestedSubscriptionOpacity: 0.2,
      withCreateInvoice: false,
      selectedCategoriesIds: "",
      selectedSubcategoriesIds: "",
      selectedServicesIds: "",
      selectedEmployeesIds: "",
      paymentsConfirmSub: null,
      paymentsSummary: null,
      glovoReportDate: null,
      calendarAttributes: [],
      excludedDaysAttributes: [],
      selectedExcludedDays: [],
      currentCategoryId: null,
      collapseState: {
        collapseGlovoReport: false,
        collapseExcludedDays: false,
      },
      selectedCategoryForGlovoReport: null,
      selectedCategoryForExcludedDays: "",
      selectedAddressBookIdsForGlovo: [],
    };
  },

  created() {
    this.formattedDateWithTime = shared.formattedDateAsNumbers;
    this.formattedDateWithoutTime = shared.formattedDateWithoutTime;
    this.glovoReportDate = moment().format("YYYY-MM-DD");
    this.calendarAttributes = [
      {
        key: "selected-date",
        dates: this.glovoReportDate,
        highlight: "green",
      },
    ];
  },
  async mounted() {
    try {
      this.loading = true;
      await this.$store.dispatch("fetchRecurringOrdersReportAvailableFilters");
      await this.fetchUserSubscriptions();
      if (this.recurringOrdersReportFilters?.categories?.[0]?.id) {
        this.currentCategoryId =
          this.recurringOrdersReportFilters.categories[0].id;
        await this.$store.dispatch(
          "fetchExcludedServicesCategoriesDays",
          this.currentCategoryId
        );
        this.updateExcludedDaysAttributes();
        this.selectedCategoryForExcludedDays = this.currentCategoryId;
        // Set default to null for "Wszystkie" option
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  },

  methods: {
    isNowDateAfterOrderStartDate(startDateToCheck) {
      if (!startDateToCheck) {
        return false;
      }

      return moment().isAfter(moment(startDateToCheck).startOf("day"), "day");
    },
    toggleCollapse(id) {
      // Toggle the state for the specific collapse area
      this.collapseState[id] = !this.collapseState[id];
    },
    formatPhoneNumber(phone) {
      if (!phone) {
        return "";
      }
      //replace all non-digits and spaces with nothing and spaces
      return phone.replace(/\D/g, "");
    },
    updateExcludedDaysAttributes() {
      // Reset selectedExcludedDays and populate with dates from the store
      this.selectedExcludedDays = [];
      if (
        this.excludedServicesCategoriesDays &&
        this.excludedServicesCategoriesDays.length
      ) {
        this.selectedExcludedDays = this.excludedServicesCategoriesDays.map(
          (day) => day.date
        );
      }

      // Update calendar attributes
      this.excludedDaysAttributes = [
        {
          key: "excluded-days",
          dates: this.selectedExcludedDays,
          highlight: "red",
          popover: {
            label: "Dzień wykluczony",
          },
        },
      ];
    },

    async saveExcludedDays() {
      if (!this.selectedCategoryForExcludedDays) {
        this.$notify({
          group: "error",
          text: "Nie wybrano kategorii usług",
        });
        return;
      }

      try {
        this.loading = true;
        await this.$store.dispatch("updateExcludedServicesCategoriesDays", {
          serviceCategoryId: this.selectedCategoryForExcludedDays,
          excludedDays: this.selectedExcludedDays.map((date) => ({
            date: moment(date).format("YYYY-MM-DDT12:00:00.000Z"),
          })),
        });

        // Notify user
        this.$notify({
          group: "success",
          text: "Wykluczone dni zostały zapisane",
        });
      } catch (error) {
        console.error(error);
        this.$notify({
          group: "error",
          text: "Wystąpił błąd podczas zapisywania wykluczonych dni",
        });
      } finally {
        this.loading = false;
      }
    },

    onDayClickExcludedDay(day) {
      const clickedDate = moment(day.date).format("YYYY-MM-DD");

      // Check if this date is already selected
      const dateIndex = this.selectedExcludedDays.findIndex(
        (date) => moment(date).format("YYYY-MM-DD") === clickedDate
      );

      if (dateIndex >= 0) {
        // If date exists, remove it
        this.selectedExcludedDays.splice(dateIndex, 1);
      } else {
        // If date doesn't exist, add it
        this.selectedExcludedDays.push(clickedDate);
      }

      // Update calendar attributes
      this.excludedDaysAttributes = [
        {
          key: "excluded-days",
          dates: this.selectedExcludedDays,
          highlight: "red",
          popover: {
            label: "Dzień wykluczony",
          },
        },
      ];
    },
    onDayClick(date) {
      this.glovoReportDate = moment(date.date).format("YYYY-MM-DD");
      this.calendarAttributes = [
        {
          key: "selected-date",
          dates: this.glovoReportDate,
          highlight: "green",
        },
      ];
    },

    generateGlovoReport() {
      if (!this.glovoReportDate) {
        this.$notify({
          group: "error",
          text: "Wybierz datę w kalendarzu",
        });
        return;
      }

      let url = `/api/recurringOrder/glovoReport/file?date=${this.glovoReportDate}`;

      // Add categoryIds parameter if specific categories are selected (not "Wszystkie")
      if (this.selectedCategoryForGlovoReport !== null) {
        url += `&categoryIds=${this.selectedCategoryForGlovoReport}`;
      } else {
        const allCategories = this.recurringOrdersReportFilters.categories.map(
          (category) => category.id
        );
        url += `&categoryIds=${allCategories.join(",")}`;
      }

      // Add addressBookIds parameter if specific address book IDs are selected (not "Wszystkie")
      if (this.selectedAddressBookIdsForGlovo.length > 0) {
        const addressBookIdsParam =
          this.selectedAddressBookIdsForGlovo.join(",");
        url += `&addressBookIds=${addressBookIdsParam}`;
      }

      axios(url, {
        method: "GET",
        headers: {
          "Content-Disposition": `attachment; filename=glovo_${this.glovoReportDate}.xls`,
          "Content-Type":
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        responseType: "blob",
      })
        .then((response) => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement("a");

          fileLink.href = fileURL;
          fileLink.setAttribute(
            "download",
            `glovo_${this.glovoReportDate}.xls`
          );
          document.body.appendChild(fileLink);
          fileLink.click();
        })
        .catch((err) => console.log(err));
    },

    handleAllAddressBookIdsChange(e) {
      if (e.target.checked) {
        // Clear all selected address book IDs when "Wszystkie" is selected
        this.selectedAddressBookIdsForGlovo = [];
      }
    },

    handleAddressBookIdChange(e, addressBookId) {
      if (e.target.checked) {
        // Add the address book ID if not already selected
        if (!this.selectedAddressBookIdsForGlovo.includes(addressBookId)) {
          this.selectedAddressBookIdsForGlovo.push(addressBookId);
        }
      } else {
        // Remove the address book ID from selection
        this.selectedAddressBookIdsForGlovo =
          this.selectedAddressBookIdsForGlovo.filter(
            (id) => id !== addressBookId
          );
      }
    },

    askForSinglePaymentConfirmation(sub) {
      this.paymentsConfirmSub = sub;
    },

    confirmSinglePayment() {
      if (!this.paymentsConfirmSub) {
        return;
      }
      // Nie zerujemy paymentsConfirmSub - okno znika z animacją i treść pytania zdążyłaby się wyczyścić
      // na oczach admina. Kolejne kliknięcie i tak nadpisze wiersz.
      this.handlePaymentsForSubscriptions(this.paymentsConfirmSub.orderId);
    },

    // Przy braku środków liczy się nie sama cena, tylko ile brakuje w portfelu - po to admin otwiera tę listę.
    summaryAmountLabel(item) {
      if (item.outcome === "NOT_ENOUGH_MONEY" && item.price != null) {
        const missing =
          item.walletAmount != null
            ? Math.round((item.price - item.walletAmount) * 100) / 100
            : item.price;
        return `brakuje ${missing} zł (z ${item.price} zł)`;
      }
      return item.price != null ? `${item.price} zł` : "-";
    },

    showPaymentsSummary(summary) {
      this.paymentsSummary = summary;
      // Okno potwierdzenia zamyka się z animacją - bez tej zwłoki bootstrap potrafi zostawić
      // przyciemnienie tła po pierwszym oknie pod drugim.
      setTimeout(() => {
        const opener = document.getElementById("paymentsSummaryOpener");
        if (opener) {
          opener.click();
        }
      }, 300);
    },

    async handlePaymentsForSubscriptions(orderId) {
      this.loading = true;
      try {
        let body = { ordersIds: [] };
        if (orderId) {
          body.ordersIds.push(orderId);
        }
        const response = await this.$store.dispatch(
          "handlePaymentsForRecurringOrders",
          body
        );

        if (response && response.message == "OK") {
          await this.fetchUserSubscriptions();
          this.showPaymentsSummary(response);
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    showSubTooltip(sub) {
      const plannedAutoChange = sub.plannedAutoChange;
      tippy(`#showTooltipButton_${sub.id}`, {
        allowHTML: true,
        content: `
        <p>Nowy status: ${plannedAutoChange.orderStateToSet}</p>
        <p>Z fakturą: ${plannedAutoChange.withCreateInvoice ? "Tak" : "Nie"}</p>
        <p>Ze zwrotem pieniędzy: ${
          plannedAutoChange.withMoneyReturn ? "Tak" : "Nie"
        }</p>
        <p>Data zmiany statusu: ${this.formattedDateWithoutTime(
          plannedAutoChange.plannedChangeDate
        )}</p>
        <p>${plannedAutoChange.reason}</p>
        `,
      });
    },
    async handleEditSub() {
      const body = {
        orderId: this.selectedSub.orderId,
        type: "CHANGE_ORDER_STATE",
        plannedChangeDate: this.editSubDate,
        orderStateToSet: this.editSubStatus,
        orderStateToSetInfo:
          this.editSubStatus === "UNREALIZED" ? this.editSubDecision : "",
        reason: this.editSubReason,
        withMoneyReturn:
          this.editSubStatus === "UNREALIZED" ? this.editSubDecision : false,
        withCreateInvoice:
          this.editSubStatus === "FINISHED" ? this.withCreateInvoice : false,
      };

      await this.$store.dispatch("createAutoPlannedChangeForOrder", body);
      document.getElementById("sub-edit-close").click();

      this.fetchUserSubscriptions();
    },
    selectSub(sub) {
      this.selectedSub = sub;
      // Reset stanu edycji notatki klienta
      this.isEditingCustomerNote = false;
      this.editedCustomerNote = sub.customerNote || "";
      this.originalCustomerNote = sub.customerNote || "";
    },
    startEditingCustomerNote() {
      this.isEditingCustomerNote = true;
      this.editedCustomerNote = this.selectedSub.customerNote || "";
      this.originalCustomerNote = this.selectedSub.customerNote || "";
    },
    cancelEditingCustomerNote() {
      this.isEditingCustomerNote = false;
      this.editedCustomerNote = this.originalCustomerNote;
    },
    async saveCustomerNote() {
      if (this.loading) return;

      try {
        // this.loading = true;

        if (this.editedCustomerNote && this.editedCustomerNote.length > 1024) {
          this.$notify({
            group: "error",
            text: "Notatka nie może przekraczać 1024 znaków",
          });
          return;
        }

        await axios.put(
          `/api/admin/orders/${this.selectedSub.orderId}/customer-note`,
          { customerNote: this.editedCustomerNote || "" }
        );

        this.selectedSub.customerNote = this.editedCustomerNote;
        this.originalCustomerNote = this.editedCustomerNote;

        const reportIndex = this.subscriptionReport.findIndex(
          (sub) => sub.orderId === this.selectedSub.orderId
        );
        if (reportIndex !== -1) {
          this.subscriptionReport[reportIndex].customerNote =
            this.editedCustomerNote;
        }

        this.isEditingCustomerNote = false;
        this.$notify({
          group: "success",
          text: "Dodatkowe informacje zostały zaktualizowane",
        });
      } catch (error) {
        console.error("Błąd podczas aktualizacji customerNote:", error);

        let errorMessage =
          "Wystąpił błąd podczas zapisywania. Spróbuj ponownie.";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }

        this.$notify({
          group: "error",
          text: errorMessage,
        });
      } finally {
        // this.loading = false;
      }
    },
    moveToProfile(userId) {
      const routeData = this.$router.resolve(`/users/id/${userId}`);
      window.open(routeData.href, "_blank");
    },

    async copyText(text) {
      const selBox = document.createElement("textarea");
      selBox.style.position = "fixed";
      selBox.style.left = "0";
      selBox.style.top = "0";
      selBox.style.opacity = "0";
      selBox.value = text;
      document.body.appendChild(selBox);
      selBox.focus();
      selBox.select();
      document.execCommand("copy");
      document.body.removeChild(selBox);
      this.$notify({
        group: "success",
        text: "ID zamówienia zostało skopiowane",
      });
    },

    // Wspólny zestaw filtrów raportu. Używa go zarówno pobieranie listy, jak i PaginationInline
    // (przez reportFilterParams), żeby zmiana strony niosła dokładnie te same filtry.
    buildReportBody() {
      const body = {
        date_from: this.selectedDateFrom
          ? this.selectedDateFrom.toISOString().split("T")[0]
          : "2021-01-01",
        date_to: this.selectedDateTo
          ? this.selectedDateTo.toISOString().split("T")[0]
          : "2999-01-01",
        type: this.filterType ? this.filterType : "",
        orderState: this.selectedOrderStatus,
        userId: this.userId,
      };
      // Add categories filter if selected
      if (this.selectedCategoriesIds) {
        body.categoriesIds = this.selectedCategoriesIds;
      }
      // Add subcategories filter if selected
      if (this.selectedSubcategoriesIds) {
        body.subcategoriesIds = this.selectedSubcategoriesIds;
      }
      // Add services filter if selected
      if (this.selectedServicesIds) {
        body.servicesIds = this.selectedServicesIds;
      }
      if (this.selectedEmployeesIds) {
        body.employeesIds = this.selectedEmployeesIds;
      }
      if (this.searchParam) {
        body.filter_param = this.searchParam;
      }
      return body;
    },

    async fetchUserSubscriptions(pageNumber) {
      try {
        this.loading = true;
        this.addedMainRow = {};
        this.showedMoreSubscriptionOrders = [];
        const body = this.buildReportBody();
        body.page_number = pageNumber || 1;
        body.on_page = this.onPage;

        await this.$store.dispatch("fetchRecurringOrdersReport", body);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    // Każda zmiana frazy wraca na pierwszą stronę - inaczej po zawężeniu wyników zostalibyśmy
    // na stronie, której już nie ma.
    handleSearch(value) {
      this.searchParam = value || "";
      this.fetchUserSubscriptions(1);
    },

    handleReportTypeForDownload(e) {
      this.selectedReportTypeForDownload = e.target.value;
    },
    handleStatusParamChange(e) {
      this.selectedOrderStatus = e.target.value;
      this.fetchUserSubscriptions();
    },

    isCategorySelected(id) {
      if (!this.selectedCategoriesIds) return false;
      const selectedIds = this.selectedCategoriesIds.split(",");
      return selectedIds.includes(id);
    },

    isSubcategorySelected(id) {
      if (!this.selectedSubcategoriesIds) return false;
      const selectedIds = this.selectedSubcategoriesIds.split(",");
      return selectedIds.includes(id);
    },

    isEmployeeSelected(id) {
      if (!this.selectedEmployeesIds) return false;
      return this.selectedEmployeesIds.split(",").includes(id);
    },

    // Backend liczy display_name (nazwa lokalu, w razie jej braku imię i nazwisko); fallback tylko na wypadek
    // profilu bez jednego i drugiego, żeby checkbox nie został bez podpisu.
    employeeDisplayName(employee) {
      if (employee.display_name) {
        return employee.display_name;
      }
      const fullName = `${employee.first_name || ""} ${
        employee.last_name || ""
      }`.trim();
      return fullName || employee.id;
    },

    isServiceSelected(id) {
      if (!this.selectedServicesIds) return false;
      const selectedIds = this.selectedServicesIds.split(",");
      return selectedIds.includes(id);
    },

    handleAllCategoriesChange(e) {
      if (e.target.checked) {
        // Uncheck all other category checkboxes
        const categoryCheckboxes = document.querySelectorAll(
          'input[type="checkbox"][data-type="category"]'
        );
        categoryCheckboxes.forEach((checkbox) => {
          if (checkbox.value !== "") {
            checkbox.checked = false;
          }
        });

        // Uncheck all subcategory checkboxes
        const subcategoryCheckboxes = document.querySelectorAll(
          'input[type="checkbox"][data-type="subcategory"]'
        );
        subcategoryCheckboxes.forEach((checkbox) => {
          if (checkbox.value !== "") {
            checkbox.checked = false;
          }
        });

        // Check the "All subcategories" option
        const allSubcategoriesCheckbox = document.querySelector(
          'input[type="checkbox"][value=""][data-type="subcategory"]'
        );
        if (allSubcategoriesCheckbox) {
          allSubcategoriesCheckbox.checked = true;
        }

        // Uncheck all service checkboxes
        const serviceCheckboxes = document.querySelectorAll(
          'input[type="checkbox"][data-type="service"]'
        );
        serviceCheckboxes.forEach((checkbox) => {
          if (checkbox.value !== "") {
            checkbox.checked = false;
          }
        });

        // Check the "All services" option
        const allServicesCheckbox = document.querySelector(
          'input[type="checkbox"][value=""][data-type="service"]'
        );
        if (allServicesCheckbox) {
          allServicesCheckbox.checked = true;
        }

        this.selectedCategoriesIds = "";
        this.selectedSubcategoriesIds = "";
        this.selectedServicesIds = "";
      }
      this.fetchUserSubscriptions();
    },

    handleCategoryChange(e, id) {
      let selectedIds = this.selectedCategoriesIds
        ? this.selectedCategoriesIds.split(",")
        : [];

      if (e.target.checked) {
        // When selecting an option other than "Wszystkie", clear the "Wszystkie" selection
        const allCheckbox = document.querySelector(
          'input[type="checkbox"][value=""][data-type="category"]'
        );
        if (allCheckbox) {
          allCheckbox.checked = false;
        }

        if (!selectedIds.includes(id)) {
          selectedIds.push(id);
        }

        // Clear subcategory and service selections as we're filtering by category
        this.selectedSubcategoriesIds = "";
        this.selectedServicesIds = "";

        // Uncheck all subcategory checkboxes except "All"
        const subcategoryCheckboxes = document.querySelectorAll(
          'input[type="checkbox"][data-type="subcategory"]'
        );
        subcategoryCheckboxes.forEach((checkbox) => {
          if (checkbox.value !== "") {
            checkbox.checked = false;
          }
        });

        // Check the "All subcategories" option
        const allSubcategoriesCheckbox = document.querySelector(
          'input[type="checkbox"][value=""][data-type="subcategory"]'
        );
        if (allSubcategoriesCheckbox) {
          allSubcategoriesCheckbox.checked = true;
        }

        // Uncheck all service checkboxes except "All"
        const serviceCheckboxes = document.querySelectorAll(
          'input[type="checkbox"][data-type="service"]'
        );
        serviceCheckboxes.forEach((checkbox) => {
          if (checkbox.value !== "") {
            checkbox.checked = false;
          }
        });

        // Check the "All services" option
        const allServicesCheckbox = document.querySelector(
          'input[type="checkbox"][value=""][data-type="service"]'
        );
        if (allServicesCheckbox) {
          allServicesCheckbox.checked = true;
        }
      } else {
        // Remove category ID from selected categories
        selectedIds = selectedIds.filter((selectedId) => selectedId !== id);
      }

      this.selectedCategoriesIds = selectedIds.join(",");
      this.fetchUserSubscriptions();
    },

    handleAllSubcategoriesChange(e) {
      if (e.target.checked) {
        // Uncheck all other subcategory checkboxes
        const subcategoryCheckboxes = document.querySelectorAll(
          'input[type="checkbox"][data-type="subcategory"]'
        );
        subcategoryCheckboxes.forEach((checkbox) => {
          if (checkbox.value !== "") {
            checkbox.checked = false;
          }
        });

        // Also uncheck all service checkboxes as we're now selecting "All subcategories"
        const serviceCheckboxes = document.querySelectorAll(
          'input[type="checkbox"][data-type="service"]'
        );
        serviceCheckboxes.forEach((checkbox) => {
          if (checkbox.value !== "") {
            checkbox.checked = false;
          }
        });

        // Check the "All services" option
        const allServicesCheckbox = document.querySelector(
          'input[type="checkbox"][value=""][data-type="service"]'
        );
        if (allServicesCheckbox) {
          allServicesCheckbox.checked = true;
        }

        this.selectedSubcategoriesIds = "";
        this.selectedServicesIds = "";
      }
      this.fetchUserSubscriptions();
    },

    handleAllServicesChange(e) {
      if (e.target.checked) {
        // Uncheck all other service checkboxes
        const serviceCheckboxes = document.querySelectorAll(
          'input[type="checkbox"][data-type="service"]'
        );
        serviceCheckboxes.forEach((checkbox) => {
          if (checkbox.value !== "") {
            checkbox.checked = false;
          }
        });

        this.selectedServicesIds = "";
      }
      this.fetchUserSubscriptions();
    },

    handleSubcategoryChange(e, id) {
      let selectedIds = this.selectedSubcategoriesIds
        ? this.selectedSubcategoriesIds.split(",")
        : [];

      if (e.target.checked) {
        // When selecting an option other than "Wszystkie", clear the "Wszystkie" selection
        const allCheckbox = document.querySelector(
          'input[type="checkbox"][value=""][data-type="subcategory"]'
        );
        if (allCheckbox) {
          allCheckbox.checked = false;
        }

        if (!selectedIds.includes(id)) {
          selectedIds.push(id);
        }
      } else {
        // Remove subcategory ID from selected subcategories
        selectedIds = selectedIds.filter((selectedId) => selectedId !== id);

        // Clear services associated with this subcategory
        if (
          this.selectedServicesIds &&
          this.recurringOrdersReportFilters.services
        ) {
          // Find the subcategory name
          const subcategory =
            this.recurringOrdersReportFilters.subcategories.find(
              (sub) => sub.id === id
            );
          if (subcategory) {
            const subcategoryName = subcategory.display_name;

            // Get services that belong to this subcategory
            const servicesForSubcategory =
              this.recurringOrdersReportFilters.services
                .filter(
                  (service) =>
                    service.subcategory_display_name === subcategoryName
                )
                .map((service) => service.id);

            // Remove these services from selected services
            let selectedServiceIds = this.selectedServicesIds
              ? this.selectedServicesIds.split(",")
              : [];
            selectedServiceIds = selectedServiceIds.filter(
              (serviceId) => !servicesForSubcategory.includes(serviceId)
            );

            // Update selected services (handle empty array case)
            this.selectedServicesIds =
              selectedServiceIds.length > 0 ? selectedServiceIds.join(",") : "";

            // Uncheck service checkboxes for this subcategory
            servicesForSubcategory.forEach((serviceId) => {
              const serviceCheckbox = document.querySelector(
                `input[type="checkbox"][value="${serviceId}"][data-type="service"]`
              );
              if (serviceCheckbox) {
                serviceCheckbox.checked = false;
              }
            });

            // If no services selected after removing, check the "All services" option
            if (this.selectedServicesIds === "") {
              const allServicesCheckbox = document.querySelector(
                'input[type="checkbox"][value=""][data-type="service"]'
              );
              if (allServicesCheckbox) {
                allServicesCheckbox.checked = true;
              }
            }
          }
        }
      }

      this.selectedSubcategoriesIds = selectedIds.join(",");
      this.fetchUserSubscriptions();
    },

    handleServiceChange(e, id) {
      let selectedIds = this.selectedServicesIds
        ? this.selectedServicesIds.split(",")
        : [];

      if (e.target.checked) {
        // When selecting an option other than "Wszystkie", clear the "Wszystkie" selection
        const allCheckbox = document.querySelector(
          'input[type="checkbox"][value=""][data-type="service"]'
        );
        if (allCheckbox) {
          allCheckbox.checked = false;
        }

        if (!selectedIds.includes(id)) {
          selectedIds.push(id);
        }
      } else {
        selectedIds = selectedIds.filter((selectedId) => selectedId !== id);
      }

      this.selectedServicesIds = selectedIds.join(",");
      this.fetchUserSubscriptions();
    },

    handleEmployeeChange(e, id) {
      let selectedIds = this.selectedEmployeesIds
        ? this.selectedEmployeesIds.split(",")
        : [];

      if (e.target.checked) {
        if (!selectedIds.includes(id)) {
          selectedIds.push(id);
        }
      } else {
        selectedIds = selectedIds.filter((selectedId) => selectedId !== id);
      }

      this.selectedEmployeesIds = selectedIds.join(",");
      this.fetchUserSubscriptions(1);
    },

    handleSubParamChange(e) {
      this.filterType = e.target.value;
      this.fetchUserSubscriptions();
    },
    async clearDates() {
      this.selectedDateFrom = null;
      this.selectedDateTo = null;
      this.fetchUserSubscriptions();
    },

    isOrderSelected(orderId) {
      return this.selectedOrders.some((o) => o.orderId === orderId);
    },

    toggleOrderSelection(sub) {
      if (this.isOrderSelected(sub.orderId)) {
        this.selectedOrders = this.selectedOrders.filter(
          (o) => o.orderId !== sub.orderId
        );
        return;
      }
      this.selectedOrders = [...this.selectedOrders, this.selectionEntry(sub)];
    },

    // Razem z id zapamiętujemy status i informację o opłaconym kolejnym tygodniu - zaznaczenie przeżywa
    // zmianę strony, więc dla wierszy spoza aktualnej listy nie dałoby się tego już odczytać.
    selectionEntry(sub) {
      return {
        orderId: sub.orderId,
        orderState: sub.orderState,
        isPaidForNextPeriod: sub.isPaidForNextPeriod,
      };
    },

    // Przycisk działa jak przełącznik: gdy wszystkie widoczne wiersze są już zaznaczone, odznacza je
    // (zaznaczenie z innych stron zostaje nietknięte).
    toggleSelectAllVisibleOrders() {
      const visible = this.visibleSubscriptionRows;
      if (!visible.length) {
        return;
      }
      const visibleIds = visible.map((s) => s.orderId);
      if (this.allVisibleOrdersSelected) {
        this.selectedOrders = this.selectedOrders.filter(
          (o) => !visibleIds.includes(o.orderId)
        );
        return;
      }
      const missing = visible
        .filter((s) => !this.isOrderSelected(s.orderId))
        .map((s) => this.selectionEntry(s));
      this.selectedOrders = [...this.selectedOrders, ...missing];
    },

    clearSelection() {
      this.selectedOrders = [];
    },

    async changeStatusForSelectedOrders() {
      if (!this.selectedOrders.length || !this.bulkStatusToChange) {
        return;
      }
      try {
        this.bulkActionLoading = true;
        const response = await axios.put(`/api/admin/orders/bulkEdit`, {
          order_state: this.bulkStatusToChange,
          orders_ids: this.selectedOrdersIds,
        });

        if (response && response.status === 200) {
          document.querySelector("#obiady-bulk-status-close").click();
          this.clearSelection();
          this.bulkStatusToChange = "";
          await this.fetchUserSubscriptions(this.pagination.page_number);
        } else {
          this.$notify({
            group: "error",
            text: "Wystąpił błąd podczas zmiany statusu. Spróbuj ponownie",
          });
        }
      } catch (error) {
        console.log(error);
        this.$notify({
          group: "error",
          text: "Wystąpił błąd podczas zmiany statusu. Spróbuj ponownie",
        });
      } finally {
        this.bulkActionLoading = false;
      }
    },

    // Wysyłamy wyłącznie zlecenia, które w wierszu miałyby aktywny przycisk "POBIERZ PŁAT." - backend
    // i tak odfiltruje resztę (bierze subskrypcje z new_created_order_id IS NULL), ale dzięki temu
    // liczba z okna dialogowego odpowiada temu, co realnie zostanie pobrane.
    async handlePaymentsForSelectedOrders() {
      if (!this.selectedPayableOrdersIds.length) {
        return;
      }
      try {
        this.bulkActionLoading = true;
        const response = await this.$store.dispatch(
          "handlePaymentsForRecurringOrders",
          { ordersIds: this.selectedPayableOrdersIds }
        );

        document.querySelector("#obiady-bulk-payments-close").click();

        if (response && response.message == "OK") {
          this.$notify({
            group: "success",
            text: "Funkcja do pobrania płatności za zamówienia cykliczne została wykonana",
          });
          this.clearSelection();
          await this.fetchUserSubscriptions(this.pagination.page_number);
        } else {
          this.$notify({
            group: "error",
            text: "Wystąpił błąd podczas pobierania płatności. Spróbuj ponownie",
          });
        }
      } catch (error) {
        console.log(error);
        this.$notify({
          group: "error",
          text: "Wystąpił błąd podczas pobierania płatności. Spróbuj ponownie",
        });
      } finally {
        this.bulkActionLoading = false;
      }
    },

    downloadSelectedReport() {
      if (this.selectedReportTypeForDownload === "NO_RENEWAL") {
        this.downloadNoRenewalReport();
        return;
      }
      this.downloadSubscriptionsReport();
    },

    // Raport zamówień idzie z DOKŁADNIE tymi filtrami, które widać w tabeli - stąd buildReportBody()
    // zamiast osobnej kopii filtrów (poprzednia wersja gubiła frazę z wyszukiwarki). Zaznaczone wiersze
    // dokładamy tylko tutaj, NIE w buildReportBody: lista w tabeli ma się przez zaznaczenie nie zmieniać.
    async downloadSubscriptionsReport() {
      const body = this.buildReportBody();
      // Bez tego zakładka pytałaby wyłącznie o karnety 5-dniowe: backend traktuje `type` jako opcjonalny
      // filtr wariantu, a przynależność do raportu wynika teraz z rodziny produktowej kategorii.
      // body.type zostawiamy puste = wszystkie warianty obiadowe.
      this.applySelectionToReportParams(body);
      await this.downloadReportFile(
        "/api/recurringOrder/report/file",
        body,
        "obiady_report.xls"
      );
    },

    // Raport bez filtrów widoku: okres bieżący (tydzień) backend liczy z dnia pobrania. Zaznaczenie
    // zawęża pulę zamówień z przeszłości, w której backend szuka kandydatów.
    async downloadNoRenewalReport() {
      await this.downloadReportFile(
        "/api/recurringOrder/report/noRenewal/file",
        this.applySelectionToReportParams({}),
        "obiady_bez_ponowienia.xls"
      );
    },

    applySelectionToReportParams(params) {
      if (this.selectedOrdersIds.length) {
        params.ordersIds = this.selectedOrdersIds.join(",");
      }
      return params;
    },

    async downloadReportFile(url, params, filename) {
      if (this.loading) {
        return;
      }
      try {
        const response = await axios(url, {
          params,
          method: "GET",
          headers: {
            "Content-Disposition": `attachment; filename=${filename}`,
            "Content-Type":
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
          responseType: "blob",
        });
        const fileURL = window.URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute("download", filename);
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
        window.URL.revokeObjectURL(fileURL);
      } catch (error) {
        console.log(error);
      }
    },

    showMoreSubscriptionsOrders(orderId) {
      if (!this.showedMoreSubscriptionOrders.includes(orderId)) {
        this.showedMoreSubscriptionOrders.push(orderId);
        // this.nestedSubscriptionOpacities[orderId] =
        //   this.nestedSubscriptionOpacity;
        // this.nestedSubscriptionOpacity += 0.15;
        // if (this.nestedSubscriptionOpacity > 1) {
        //   this.nestedSubscriptionOpacity = 0.3;
        // }
      }
    },

    hideMoreSubscriptionsOrders(orderId) {
      this.showedMoreSubscriptionOrders =
        this.showedMoreSubscriptionOrders.filter((o) => o !== orderId);
    },

    async fetchInvoiceLink(id) {
      this.invoiceLink = null;
      try {
        await axios
          .get(`/api/recurringOrder/${id}/invoice`)
          .then((response) => {
            this.invoiceLink = response.data;
          });
      } catch (error) {
        this.invoiceLink = null;
      }
      if (!this.invoiceLink) {
        this.$notify({
          group: "error",
          text: "Brak faktury dla tego zamówienia",
        });
        return;
      }

      document.getElementById(`downloadLink${id}`).click();
    },

    async copyInvoiceLinkToClipboard(id) {
      let link = "";
      try {
        await axios
          .get(`/api/recurringOrder/${id}/invoice`)
          .then((response) => {
            link = response.data;
          });
      } catch (error) {
        link = null;
      }
      if (!link) {
        this.$notify({
          group: "error",
          text: "Brak faktury dla tego zamówienia",
        });
        return;
      }

      navigator.clipboard.writeText(link);
      this.$notify({
        group: "success",
        text: "Link do faktury został skopiowany",
      });
    },
    resetMainRows() {
      this.addedMainRow = {};
    },
    async handleCancelSubscription() {
      if (this.loading || this.cancelSubscriptionLoading) return;

      try {
        this.cancelSubscriptionLoading = true;

        const response = await axios.post(
          `/api/recurringOrder/cancel/${this.selectedSub.orderId}`
        );

        // Close modal
        const closeButton = document.getElementById("sub-cancel-confirm-close");
        if (closeButton) {
          closeButton.click();
        }

        // Show success notification
        if (response.data.moneyReturned) {
          this.$notify({
            group: "success",
            text: `Abonament został anulowany. Zwrócono ${response.data.refundAmount} PLN na portfel klienta.`,
          });
        } else {
          this.$notify({
            group: "success",
            text: "Abonament został anulowany pomyślnie.",
          });
        }

        // Refresh the subscriptions list
        await this.fetchUserSubscriptions();
      } catch (error) {
        console.error("Error canceling subscription:", error);

        let errorMessage =
          "Wystąpił błąd podczas anulowania abonamentu. Spróbuj ponownie.";
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }

        this.$notify({
          group: "error",
          text: errorMessage,
        });
      } finally {
        this.cancelSubscriptionLoading = false;
      }
    },
  },

  computed: {
    excludedServicesCategoriesDays() {
      return this.$store.getters.getExcludedServicesCategoriesDays;
    },
    recurringOrdersReportFilters() {
      return this.$store.getters.getRecurringOrdersReportFilters;
    },
    glovoAddressBookProfiles() {
      const profiles = this.recurringOrdersReportFilters.profilesWithService || [];
      if (this.selectedCategoryForGlovoReport === null) {
        return profiles;
      }
      return profiles.filter((profile) =>
        (profile.category_ids || "")
          .split(",")
          .includes(this.selectedCategoryForGlovoReport)
      );
    },
    filteredSubcategories() {
      // Return empty array if no subcategories available
      if (!this.recurringOrdersReportFilters.subcategories) {
        return [];
      }

      // Return all subcategories if no category selected
      if (!this.selectedCategoriesIds) {
        return this.recurringOrdersReportFilters.subcategories;
      }

      // Otherwise, filter subcategories by selected categories
      const selectedCategoriesIds = this.selectedCategoriesIds.split(",");

      return this.recurringOrdersReportFilters.subcategories.filter(
        (subcategory) => {
          // Check if the subcategory belongs to any of the selected categories
          if (
            subcategory.category_id &&
            selectedCategoriesIds.includes(subcategory.category_id)
          ) {
            return true;
          }
          return false;
        }
      );
    },
    filteredServices() {
      // Return all services if no filters or services available
      if (!this.recurringOrdersReportFilters.services) {
        return [];
      }

      // If categories are selected but no subcategories, filter by categories.
      // Dopasowanie idzie po category_id uslugi, nie przez jej podkategorie - usluga karnetowa bez
      // podkategorii (S3 "KARNET TESTOWY") wypadala z listy, bo nie miala czym sie zmatchowac.
      if (this.selectedCategoriesIds && !this.selectedSubcategoriesIds) {
        const selectedCategoriesIds = this.selectedCategoriesIds.split(",");
        return this.recurringOrdersReportFilters.services.filter(
          (service) =>
            service.category_id &&
            selectedCategoriesIds.includes(service.category_id)
        );
      }

      // Return all services if no subcategory selected
      if (!this.selectedSubcategoriesIds) {
        return this.recurringOrdersReportFilters.services;
      }

      // Otherwise, filter services by selected subcategories
      const selectedSubcategoriesDisplayNames =
        this.selectedSubcategoriesDisplayNames;

      if (selectedSubcategoriesDisplayNames.length === 0) {
        return this.recurringOrdersReportFilters.services;
      }

      return this.recurringOrdersReportFilters.services.filter((service) =>
        selectedSubcategoriesDisplayNames.includes(
          service.subcategory_display_name
        )
      );
    },
    selectedSubcategoriesDisplayNames() {
      if (
        !this.selectedSubcategoriesIds ||
        !this.recurringOrdersReportFilters.subcategories
      ) {
        return [];
      }

      const selectedSubcategoriesArray =
        this.selectedSubcategoriesIds.split(",");

      return this.recurringOrdersReportFilters.subcategories
        .filter((sub) => selectedSubcategoriesArray.includes(sub.id))
        .map((sub) => sub.display_name);
    },
    decisionOptions() {
      if (this.editSubStatus === "UNREALIZED") {
        return [
          { value: false, label: "Anuluj bez zwrotu pieniędzy" },
          { value: true, label: "Anuluj ze zwrotem pieniędzy" },
        ];
      } else {
        return [];
      }
    },
    withInvoiceOptions() {
      if (this.editSubStatus === "FINISHED") {
        return [
          { value: true, label: "Z fakturą" },
          { value: false, label: "Bez faktury" },
        ];
      } else {
        return [];
      }
    },
    statuses() {
      return ["ACCEPTED", "FINISHED", "UNREALIZED"];
    },
    // Statusy zmiany masowej takie same jak w Historii usług - to te, które obsługuje
    // PUT /admin/orders/bulkEdit (z ACCEPTED da się wyjść, wrócić do niego już nie).
    bulkStatuses() {
      return ["UNREALIZED", "REJECTED", "FINISHED"];
    },
    // Wiersze faktycznie widoczne w tabeli: główne + tygodnie rozwiniętych subskrypcji.
    visibleSubscriptionRows() {
      if (!this.subscriptionReport) {
        return [];
      }
      return this.subscriptionReport.filter(
        (s) =>
          s.isMainRow ||
          this.showedMoreSubscriptionOrders.includes(`${s.firstOrderId}`)
      );
    },
    allVisibleOrdersSelected() {
      const visible = this.visibleSubscriptionRows;
      return (
        visible.length > 0 && visible.every((s) => this.isOrderSelected(s.orderId))
      );
    },
    selectedOrdersIds() {
      return this.selectedOrders.map((o) => o.orderId);
    },
    selectedAcceptedOrdersIds() {
      return this.selectedOrders
        .filter((o) => o.orderState === "ACCEPTED")
        .map((o) => o.orderId);
    },
    // Do pobrania płatności kwalifikuje się dokładnie to, co ma w wierszu przycisk "POBIERZ PŁAT.":
    // zlecenie ACCEPTED bez opłaconego kolejnego tygodnia. isPaidForNextPeriod to po stronie bazy
    // recurring_orders.new_created_order_id != null, czyli ten sam warunek, którego backend używa
    // przy wyborze subskrypcji do odnowienia - dzięki temu licznik w oknie mówi prawdę.
    selectedPayableOrdersIds() {
      return this.selectedOrders
        .filter((o) => o.orderState === "ACCEPTED" && !o.isPaidForNextPeriod)
        .map((o) => o.orderId);
    },
    pagination() {
      return (
        this.$store.getters.getRecurringOrdersReportPagination || {
          page_number: 1,
          on_page: this.onPage,
          total: 0,
        }
      );
    },
    // Wynik przebiegu w grupach; puste grupy odpadają, żeby okno nie pokazywało pustych tabel.
    // Kolejność nieprzypadkowa: na górze te, które wymagają reakcji admina (brak środków), na dole
    // te, które po prostu się udały.
    paymentsSummaryGroups() {
      if (!this.paymentsSummary || !this.paymentsSummary.items) {
        return [];
      }
      const labels = [
        { outcome: "NOT_ENOUGH_MONEY", label: "Brak środków" },
        { outcome: "SUBSCRIPTION_ENDED", label: "Abonament zakończony" },
        { outcome: "OUTSIDE_PAYMENT_WINDOW", label: "Poza okresem pobrania" },
        { outcome: "WEEK_ALREADY_ORDERED", label: "Tydzień już zamówiony" },
        { outcome: "SKIPPED", label: "Pominięte" },
        { outcome: "CARD_CHARGE_REQUESTED", label: "Obciążenie karty zlecone" },
        { outcome: "RENEWED", label: "Przedłużone" },
      ];
      return labels
        .map((group) => ({
          ...group,
          items: this.paymentsSummary.items.filter(
            (item) => item.outcome === group.outcome
          ),
        }))
        .filter((group) => group.items.length > 0);
    },

    // Filtry raportu, których PaginationInline nie ma w swoich stałych slotach - bez nich zmiana
    // strony pobrałaby listę nieprzefiltrowaną.
    reportFilterParams() {
      return this.buildReportBody();
    },
    subscriptionReport() {
      this.resetMainRows();
      let reports = this.$store.getters.getRecurringOrdersReport;

      if (reports && reports.length > 0) {
        reports.forEach((r, index) => {
          if (!this.addedMainRow[`${r.firstOrderId}`]) {
            this.addedMainRow[`${r.firstOrderId}`] = 1;
            r.isMainRow = true;
            if (
              reports[index - 1] &&
              reports[index - 1].firstOrderId !== r.firstOrderId
            ) {
              r.isFirstOrderRow = true;
            }
            if (
              reports[index + 1] &&
              reports[index + 1].firstOrderId !== r.firstOrderId
            ) {
              r.isLastOrderRow = true;
            }
          } else {
            r.isMainRow = false;
            this.addedMainRow[`${r.firstOrderId}`] =
              this.addedMainRow[`${r.firstOrderId}`] + 1;
            if (
              reports[index + 1] &&
              reports[index + 1].firstOrderId !== r.firstOrderId
            ) {
              r.isLastOrderRow = true;
            }
          }
        });
      }

      return reports;
    },
    filteringParams() {
      return this.$store.getters.getFilteringParams;
    },
  },

  watch: {
    // A checked ID hidden by the new category would still be sent with the report.
    glovoAddressBookProfiles(profiles) {
      const visibleIds = profiles.map((p) => p.glovo_address_book_id);
      this.selectedAddressBookIdsForGlovo =
        this.selectedAddressBookIdsForGlovo.filter((id) =>
          visibleIds.includes(id)
        );
    },
    selectedDateFrom() {
      if (this.selectedDateFrom) {
        this.fetchUserSubscriptions();
      }
    },
    selectedDateTo() {
      if (this.selectedDateTo) {
        this.fetchUserSubscriptions();
      }
    },
    excludedServicesCategoriesDays: {
      handler() {
        this.updateExcludedDaysAttributes();
      },
      deep: true,
    },
    selectedCategoryForExcludedDays() {
      // When category selection changes, update the current category ID and fetch excluded days
      if (this.selectedCategoryForExcludedDays) {
        this.$store.dispatch(
          "fetchExcludedServicesCategoriesDays",
          this.selectedCategoryForExcludedDays
        );
      } else if (this.recurringOrdersReportFilters?.categories?.[0]?.id) {
        this.selectedCategoryForExcludedDays =
          this.recurringOrdersReportFilters.categories[0].id;
        this.$store.dispatch(
          "fetchExcludedServicesCategoriesDays",
          this.selectedCategoryForExcludedDays
        );
      }
    },
  },
};
</script>

<style scoped>
/* Table Styles */
.first-order-row {
  border-top: 2px solid black;
}
.last-order-row {
  border-bottom: 2px solid black;
}
.nested-subscription {
  border-right: 2px solid black;
  border-left: 2px solid black;
}
.not-renewed {
  background: rgba(226, 3, 126, 0.07);
}

.inline-buttons {
  display: flex;
  gap: 1rem;
}

/* Layout */
.reportTable {
  width: 100%;
  margin-bottom: 2rem;
}
.options-container {
  display: flex;
  gap: 20px;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}
.filters-column {
  flex: 1;
  min-width: 300px;
  padding: 0.75rem;
  background-color: #f9f9f9;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.calendars-column {
  flex: 1;
  min-width: 300px;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
/* 1:1 z pozostałymi listami panelu (Users.vue): tabela jest przyklejona do paska paginacji i nie
   ma własnego scrolla poziomego - szeroka tabela rozpycha stronę, więc pasek przewija się razem
   z nią i zawsze ma tę samą szerokość. Własny overflow zostaje tylko na karcie użytkownika,
   gdzie widok jest osadzony w wąskiej zakładce i nie ma nad sobą paska. */
/* App.vue ma globalne `div { padding: 5px }`, więc KAŻDY poziom zagnieżdżenia wcina zawartość
   o 5 px z każdej strony i spycha ją o 5 px w dół. W pozostałych listach tabela jest bezpośrednim
   rodzeństwem nagłówka i offset się zgadza; tutaj leży dwa divy głębiej (.table-container i wrapper
   z v-if="!loading"), przez co odstawała od paska o 10 px i była o 10 px węższa z każdej strony.
   Dlatego oba te divy mają wyzerowany padding - inaczej pasek i tabela nigdy się nie zejdą. */
.table-container {
  margin-top: 0;
  padding: 0;
}
.table-container > div {
  padding: 0;
}

/* Skopiowane z TableHeader.vue, żeby ekran obiadów wyglądał jak pozostałe listy panelu.
   Style tam są scoped, więc nie da się ich odziedziczyć - przy zmianach wyglądu list
   trzeba ruszyć oba miejsca. */
.searchPanel {
  display: flex;
  align-items: center;
  min-height: 80px;
  justify-content: space-between;
}
.searchPanel h1 {
  margin-bottom: 0;
  margin-right: 20px;
}
.title {
  min-width: 100px;
}
.flex-inline {
  display: flex;
  align-items: center;
}
.header {
  padding: 0;
  display: flex;
  background: #f7f8f9;
  color: #93a3ad;
  height: auto;
  min-height: 50px;
  border-radius: 18px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;

  -webkit-box-shadow: 0px 0px 30px 5px rgba(176, 176, 176, 1);
  -moz-box-shadow: 0px 0px 30px 5px rgba(176, 176, 176, 1);
  box-shadow: 0px 0px 30px 5px rgba(176, 176, 176, 1);
  flex-wrap: wrap;
  justify-content: space-between;
  font-size: 11px;
  align-items: center;
}
.loader {
  width: 20px;
  height: 20px;
  margin-left: 10px;
}
.headerActions {
  display: flex;
  align-items: center;
  padding-right: 15px;
}
/* SearchInput ma własne (scoped) style - prostokątną ramkę i wysokość 40 px - a na pozostałych
   ekranach wyszukiwarka jest "pigułką" na szarym tle. Stylów scoped komponentu nie da się
   nadpisać zwykłym selektorem, stąd >>> (konwencja tego repo, patrz .full-width-calendar niżej).
   Wartości 1:1 z TableHeader.vue. */
/* Bez ustawiania border - ramkę daje bootstrapowy .form-control, tak samo jak w TableHeader.
   Nadpisanie jej na none sprawiało, że pole zlewało się z tłem. */
.searchPanel >>> .searchUserPanel input {
  border-radius: 50px !important;
  background: #f2f2f2;
  color: #090741;
  height: 32px;
  font-size: 13px;
}
.searchPanel >>> .left-addon input {
  padding-left: 30px;
}
/* Przycisk siedzi w pasku obok paginacji, więc musi być niski i jednoliniowy - bootstrapowy
   .btn ma na tyle duży padding, że rozpycha pasek ponad jego min-height. */
/* Oba stany MUSZĄ mieć identyczne wymiary, żeby na hover zmieniał się wyłącznie kolor tła.
   App.vue ustawia font-size dwiema regułami z !important - 10px dla .btn-outline-dark i 10px dla
   jego :hover - więc rozmiar trzeba narzucić tu również z !important i w obu stanach naraz.
   Podanie go tylko w :hover dawało efekt odwrotny do zamierzonego: przycisk rósł po najechaniu. */
.downloadReportButton,
.downloadReportButton:hover {
  margin: 0;
  padding: 6px 16px;
  font-size: 10px !important;
  white-space: nowrap;
}
.headerPaymentsButton,
.headerPaymentsButton:hover {
  margin: 0 20px 0 0;
  padding: 6px 16px;
  font-size: 10px !important;
  white-space: nowrap;
}

/* Kolumna zaznaczania: przyciski akcji masowych siedzą w nagłówku pierwszej kolumny (jak w Historii
   usług), więc muszą zostać w jednej linii - inaczej rozpychają wiersz nagłówka tabeli. */
.selectionColumn {
  white-space: nowrap;
}
.selectionActions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.selectionButton,
.selectionButton:hover {
  margin: 0;
  padding: 4px 12px;
  font-size: 10px !important;
  white-space: nowrap;
}
.order-checkbox {
  cursor: pointer;
  width: 16px;
  height: 16px;
}
.bulkStatusHint {
  color: #b3261e;
}
.reportSelectionHint {
  margin-right: 10px;
  font-size: 11px;
  color: #4a5560;
  white-space: nowrap;
}

/* Pasek wyboru raportu: etykieta, lista i przycisk w jednej linii. Wysokości trzymamy niskie z tego
   samego powodu co przy przycisku - nagłówek ma min-height 50 px i łatwo go rozepchać. */
.reportLabel {
  margin: 0 10px 0 0;
  font-size: 12px;
  color: #4a5560;
  white-space: nowrap;
}
.reportSelect {
  min-width: 240px;
  height: 32px;
  padding: 0 12px;
  margin-right: 10px;
  border-radius: 50px;
  font-size: 12px;
  color: #4a5560;
}

/* Filter Components */
.filter-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
}
.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.1rem;
}
.filter-item p {
  margin-bottom: 0.3rem;
  margin-top: 0;
}
.filter-select {
  min-width: 200px;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #ccc;
}
.date-filter {
  margin-bottom: 0.75rem;
}
.datepickers {
  display: flex;
  gap: 0.5rem;
}
.datepicker-wrapper {
  position: relative;
  display: inline-block;
  padding-top: 0;
  padding-bottom: 0;
}
.datepicker-wrapper div {
}
.datepicker-input {
  padding: 0;
  border-radius: 4px;
}
.clear-date-btn {
  position: absolute;
  right: 25px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}
.clear-date-btn:hover {
  background-color: #f1f1f1;
  color: #333;
}

/* Calendar Components */
.calendar-box {
  margin-bottom: 1.5rem;
}
.calendar-button {
  width: 100%;
  margin-bottom: 0.5rem;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.calendar-button i {
  font-size: 16px;
  transition: transform 0.2s ease;
}
.calendar-container {
  width: 100%;
  margin-bottom: 0.5rem;
}
.calendar-container div {
  padding: 0 !important;
}
.full-width-calendar {
  width: 100% !important;
  max-width: 100% !important;
}
.full-width-calendar >>> .vc-container {
  width: 100% !important;
  max-width: 100% !important;
}

/* Potwierdzenie pobrania płatności */
.confirm-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.confirm-question {
  font-size: 18px;
  margin-bottom: 1rem;
  text-align: center;
}
.summary-empty {
  font-size: 16px;
  text-align: center;
  margin: 0;
}
.summary-counters {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}
.summary-counter {
  font-size: 14px;
}
.summary-group {
  margin-bottom: 1.5rem;
}
.summary-group-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.summary-table {
  font-size: 12px;
}
.summary-table th {
  color: #93a3ad;
  font-weight: 600;
}
.confirm-hint {
  font-size: 13px;
  color: #93a3ad;
  margin-bottom: 2rem;
  text-align: center;
}
.confirm-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

/* Multi-select */
.multi-select-container {
  border: 1px solid #ccc;
  border-radius: 4px;
  max-height: 150px;
  overflow-y: auto;
  background-color: white;
  width: 100%;
  padding: 5px;
}
.multi-select-item {
  padding: 5px;
  border-bottom: 1px solid #f1f1f1;
}
.multi-select-item:last-child {
  border-bottom: none;
}
.multi-select-item label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0;
  width: 100%;
}
.multi-select-item input[type="checkbox"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border: 1px solid #ccc;
  border-radius: 2px;
  margin-right: 8px;
  position: relative;
  cursor: pointer;
  vertical-align: middle;
  outline: none;
  flex-shrink: 0;
}
.multi-select-item input[type="checkbox"]:checked {
  background: linear-gradient(
    36.47deg,
    rgb(251, 137, 40) 10.51%,
    #e50582 109.44%
  ) !important;
  border-color: transparent;
}
.multi-select-item input[type="checkbox"]:checked::after {
  content: "✓";
  position: absolute;
  color: white;
  font-size: 12px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Utils */
.arrow-icon {
  cursor: pointer;
  font-size: 20px;
}
.profile-icon {
  cursor: pointer;
}

/* Edit Modal */
.edit-container {
  text-align: left;
}
.edit-row {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1rem;
}
.edit-container-label {
  width: 120px;
}
.edit-row select {
  width: 180px;
}

.customer-note-container {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 6px;
}

.customer-note-edit {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.customer-note-edit textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  font-size: 14px;
}

.customer-note-edit textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  color: #666;
}

.customer-note-edit textarea.note-editing {
  border-color: #007bff;
  background-color: white;
}

.customer-note-edit textarea:focus {
  outline: none;
  border-color: #0056b3;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.note-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.character-counter {
  text-align: right;
  font-size: 0.85rem;
  color: #666;
  margin: 0;
}

/* Button Styles */
button {
  border-radius: 4px;
}

button.btn-outline-dark:hover {
  background-color: #343a40;
  color: white;
}
</style>
